import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import adminApiService from '../../services/adminApiService';
import toast from 'react-hot-toast';
import {
  CogIcon,
  ShieldCheckIcon,
  BellIcon,
  GlobeAltIcon,
  KeyIcon,
  CircleStackIcon,
  CloudIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon
} from '@heroicons/react/24/outline';

interface SystemSettings {
  general: {
    siteName: string;
    siteDescription: string;
    supportEmail: string;
    maintenanceMode: boolean;
    registrationEnabled: boolean;
    emailVerificationRequired: boolean;
  };
  security: {
    passwordMinLength: number;
    requireTwoFactor: boolean;
    sessionTimeout: number;
    maxLoginAttempts: number;
    ipWhitelist: string[];
    sslRequired: boolean;
  };
  notifications: {
    emailNotifications: boolean;
    smsNotifications: boolean;
    pushNotifications: boolean;
    adminAlerts: boolean;
    userWelcomeEmails: boolean;
    systemMaintenanceAlerts: boolean;
  };
  integrations: {
    openaiApiKey: string;
    stripePublishableKey: string;
    stripeSecretKey: string;
    stripeWebhookSecret: string;
    paypalClientId: string;
    paypalClientSecret: string;
    paypalWebhookId: string;
    nowpaymentsApiKey: string;
    nowpaymentsIpnSecret: string;
    twilioAccountSid: string;
    twilioAuthToken: string;
    googleAnalyticsId: string;
  };
  limits: {
    maxUsersPerDay: number;
    maxJobPostingsPerMonth: number;
    maxCoursesPerUser: number;
    maxFileUploadSize: number;
    apiRateLimit: number;
  };
  backup: {
    autoBackupEnabled: boolean;
    backupFrequency: 'daily' | 'weekly' | 'monthly';
    retentionDays: number;
    lastBackup?: Date;
    backupLocation: string;
  };
}

const SystemSettings: React.FC = () => {
  const { adminUser, hasPermission } = useAdmin();
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('general');
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      
      const response = await adminApiService.getSystemConfiguration();
      if (response.success && response.data) {
        // Transform backend data to frontend format
        const backendConfig = response.data;
        const transformedSettings: SystemSettings = {
          general: {
            siteName: backendConfig.general?.siteName || 'Workazon',
            siteDescription: backendConfig.general?.siteDescription || 'Professional job posting and university course platform',
            supportEmail: backendConfig.email?.replyTo || 'support@workazon.com',
            maintenanceMode: backendConfig.general?.maintenanceMode || false,
            registrationEnabled: backendConfig.general?.registrationEnabled || true,
            emailVerificationRequired: backendConfig.security?.requireEmailVerification || true
          },
          security: {
            passwordMinLength: backendConfig.security?.passwordMinLength || 8,
            requireTwoFactor: backendConfig.security?.twoFactorRequired || false,
            sessionTimeout: Math.floor((backendConfig.security?.sessionTimeout || 3600) / 60), // Convert seconds to minutes
            maxLoginAttempts: backendConfig.security?.maxLoginAttempts || 5,
            ipWhitelist: [],
            sslRequired: true
          },
          notifications: {
            emailNotifications: backendConfig.email?.notificationEmailEnabled || true,
            smsNotifications: backendConfig.features?.smsNotifications || false,
            pushNotifications: backendConfig.features?.realTimeUpdates || true,
            adminAlerts: true,
            userWelcomeEmails: backendConfig.email?.welcomeEmailEnabled || true,
            systemMaintenanceAlerts: true
          },
          integrations: {
            openaiApiKey: backendConfig.integrations?.openaiApiKey ? '••••••••' : '',
            stripePublishableKey: backendConfig.integrations?.stripePublishableKey || '',
            stripeSecretKey: backendConfig.integrations?.stripeSecretKey ? '••••••••' : '',
            stripeWebhookSecret: backendConfig.integrations?.stripeWebhookSecret ? '••••••••' : '',
            paypalClientId: backendConfig.integrations?.paypalClientId || '',
            paypalClientSecret: backendConfig.integrations?.paypalClientSecret ? '••••••••' : '',
            paypalWebhookId: backendConfig.integrations?.paypalWebhookId || '',
            nowpaymentsApiKey: backendConfig.integrations?.nowpaymentsApiKey ? '••••••••' : '',
            nowpaymentsIpnSecret: backendConfig.integrations?.nowpaymentsIpnSecret ? '••••••••' : '',
            twilioAccountSid: backendConfig.integrations?.twilioAccountSid ? '••••••••' : '',
            twilioAuthToken: backendConfig.integrations?.twilioAuthToken ? '••••••••' : '',
            googleAnalyticsId: backendConfig.analytics?.trackingEnabled ? 'GA-••••••••' : ''
          },
          limits: {
            maxUsersPerDay: backendConfig.general?.maxUsersPerDay || 1000,
            maxJobPostingsPerMonth: 10000,
            maxCoursesPerUser: 50,
            maxFileUploadSize: Math.floor((backendConfig.storage?.maxFileSize || 10485760) / 1048576), // Convert bytes to MB
            apiRateLimit: backendConfig.api?.requestsPerHour || 1000
          },
          backup: {
            autoBackupEnabled: backendConfig.storage?.backupEnabled || true,
            backupFrequency: backendConfig.storage?.backupFrequency || 'daily',
            retentionDays: backendConfig.storage?.retentionPeriod || 30,
            lastBackup: new Date(),
            backupLocation: 'Firebase Storage'
          }
        };
        setSettings(transformedSettings);
      } else {
        throw new Error('Failed to load configuration');
      }
    } catch (error: any) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load system settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;
    
    try {
      setSaving(true);
      
      // Transform frontend data back to backend format
      const backendConfig = {
        general: {
          siteName: settings.general.siteName,
          siteDescription: settings.general.siteDescription,
          maintenanceMode: settings.general.maintenanceMode,
          registrationEnabled: settings.general.registrationEnabled,
          loginEnabled: true,
          maxUsersPerDay: settings.limits.maxUsersPerDay,
          timezone: 'UTC'
        },
        features: {
          jobPosting: true,
          courseEnrollment: true,
          backgroundChecks: true,
          aiValidation: true,
          socialMediaIntegration: false,
          emailNotifications: settings.notifications.emailNotifications,
          smsNotifications: settings.notifications.smsNotifications,
          realTimeUpdates: settings.notifications.pushNotifications
        },
        security: {
          passwordMinLength: settings.security.passwordMinLength,
          requireEmailVerification: settings.general.emailVerificationRequired,
          requirePhoneVerification: false,
          sessionTimeout: settings.security.sessionTimeout * 60, // Convert minutes to seconds
          maxLoginAttempts: settings.security.maxLoginAttempts,
          lockoutDuration: 900,
          twoFactorRequired: settings.security.requireTwoFactor
        },
        email: {
          provider: 'smtp2go',
          fromName: 'Workazon Team',
          fromEmail: process.env.REACT_APP_SMTP_FROM || 'noreply@workazon.com',
          replyTo: settings.general.supportEmail,
          welcomeEmailEnabled: settings.notifications.userWelcomeEmails,
          notificationEmailEnabled: settings.notifications.emailNotifications,
          marketingEmailEnabled: false
        },
        payments: {
          provider: 'stripe',
          currency: 'usd',
          taxRate: 0.08,
          trialPeriodDays: 14,
          allowCancellation: true,
          refundWindow: 30
        },
        ai: {
          cvGenerationEnabled: false,
          personalityAnalysisEnabled: false,
          backgroundCheckEnabled: true,
          scoringEnabled: false,
          validationThreshold: 0.8
        },
        moderation: {
          autoModerationEnabled: true,
          contentScanningEnabled: true,
          flaggedContentReview: true,
          communityReporting: true,
          moderationQueueLimit: 100
        },
        analytics: {
          trackingEnabled: true,
          dataRetentionDays: 365,
          anonymizeData: true,
          shareAggregatedData: false
        },
        api: {
          rateLimitEnabled: true,
          requestsPerMinute: 60,
          requestsPerHour: settings.limits.apiRateLimit,
          requestsPerDay: 10000,
          apiKeysRequired: false
        },
        storage: {
          maxFileSize: settings.limits.maxFileUploadSize * 1048576, // Convert MB to bytes
          allowedFileTypes: ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'],
          backupEnabled: settings.backup.autoBackupEnabled,
          backupFrequency: settings.backup.backupFrequency,
          retentionPeriod: settings.backup.retentionDays
        },
        integrations: {
          openaiApiKey: settings.integrations.openaiApiKey === '••••••••' ? undefined : settings.integrations.openaiApiKey,
          stripePublishableKey: settings.integrations.stripePublishableKey,
          stripeSecretKey: settings.integrations.stripeSecretKey === '••••••••' ? undefined : settings.integrations.stripeSecretKey,
          stripeWebhookSecret: settings.integrations.stripeWebhookSecret === '••••••••' ? undefined : settings.integrations.stripeWebhookSecret,
          paypalClientId: settings.integrations.paypalClientId,
          paypalClientSecret: settings.integrations.paypalClientSecret === '••••••••' ? undefined : settings.integrations.paypalClientSecret,
          paypalWebhookId: settings.integrations.paypalWebhookId,
          nowpaymentsApiKey: settings.integrations.nowpaymentsApiKey === '••••••••' ? undefined : settings.integrations.nowpaymentsApiKey,
          nowpaymentsIpnSecret: settings.integrations.nowpaymentsIpnSecret === '••••••••' ? undefined : settings.integrations.nowpaymentsIpnSecret,
          twilioAccountSid: settings.integrations.twilioAccountSid === '••••••••' ? undefined : settings.integrations.twilioAccountSid,
          twilioAuthToken: settings.integrations.twilioAuthToken === '••••••••' ? undefined : settings.integrations.twilioAuthToken
        }
      };

      const response = await adminApiService.updateSystemConfiguration(backendConfig);
      
      if (response.success) {
        toast.success('Settings saved successfully');
        setUnsavedChanges(false);
        // Refresh settings to get the latest data
        await fetchSettings();
      } else {
        throw new Error(response.message || 'Failed to save settings');
      }
    } catch (error: any) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings: ' + (error.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (category: keyof SystemSettings, key: string, value: any) => {
    if (!settings) return;
    
    setSettings({
      ...settings,
      [category]: {
        ...settings[category],
        [key]: value
      }
    });
    setUnsavedChanges(true);
  };

  const handleResetSettings = async () => {
    if (!window.confirm('Are you sure you want to reset all settings to defaults? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      const response = await adminApiService.resetSystemConfiguration();
      
      if (response.success) {
        toast.success('Settings reset to defaults successfully');
        setUnsavedChanges(false);
        // Refresh settings to get the reset data
        await fetchSettings();
      } else {
        throw new Error(response.message || 'Failed to reset settings');
      }
    } catch (error: any) {
      console.error('Failed to reset settings:', error);
      toast.error('Failed to reset settings: ' + (error.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleRunBackup = async () => {
    try {
      // This would call a real API endpoint
      toast.success('Backup initiated successfully');
      // Update last backup time
      if (settings) {
        setSettings({
          ...settings,
          backup: {
            ...settings.backup,
            lastBackup: new Date()
          }
        });
      }
    } catch (error: any) {
      toast.error('Failed to initiate backup');
    }
  };

  const tabs = [
    { id: 'general', name: 'General', icon: CogIcon },
    { id: 'security', name: 'Security', icon: ShieldCheckIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
    { id: 'integrations', name: 'Integrations', icon: GlobeAltIcon },
    { id: 'limits', name: 'Limits', icon: ExclamationTriangleIcon },
    { id: 'backup', name: 'Backup', icon: CircleStackIcon }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-12">
        <CogIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Failed to load settings</h3>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">System Settings</h1>
          <p className="mt-2 text-sm text-gray-700">
            Configure platform-wide settings and preferences
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none flex gap-3">
          <button
            type="button"
            onClick={handleResetSettings}
            disabled={saving}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto disabled:opacity-50"
          >
            {saving ? 'Resetting...' : 'Reset to Defaults'}
          </button>
          {unsavedChanges && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {/* General Settings */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">General Settings</h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Site Name</label>
                  <input
                    type="text"
                    value={settings.general.siteName}
                    onChange={(e) => handleSettingChange('general', 'siteName', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Support Email</label>
                  <input
                    type="email"
                    value={settings.general.supportEmail}
                    onChange={(e) => handleSettingChange('general', 'supportEmail', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Site Description</label>
                <textarea
                  rows={3}
                  value={settings.general.siteDescription}
                  onChange={(e) => handleSettingChange('general', 'siteDescription', e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.general.maintenanceMode}
                    onChange={(e) => handleSettingChange('general', 'maintenanceMode', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Maintenance Mode
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.general.registrationEnabled}
                    onChange={(e) => handleSettingChange('general', 'registrationEnabled', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Registration Enabled
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.general.emailVerificationRequired}
                    onChange={(e) => handleSettingChange('general', 'emailVerificationRequired', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Email Verification Required
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Security Settings</h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Password Minimum Length</label>
                  <input
                    type="number"
                    min="6"
                    max="20"
                    value={settings.security.passwordMinLength}
                    onChange={(e) => handleSettingChange('security', 'passwordMinLength', parseInt(e.target.value))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Session Timeout (minutes)</label>
                  <input
                    type="number"
                    min="5"
                    max="10080"
                    value={settings.security.sessionTimeout}
                    onChange={(e) => handleSettingChange('security', 'sessionTimeout', parseInt(e.target.value))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Max Login Attempts</label>
                  <input
                    type="number"
                    min="3"
                    max="10"
                    value={settings.security.maxLoginAttempts}
                    onChange={(e) => handleSettingChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.security.requireTwoFactor}
                    onChange={(e) => handleSettingChange('security', 'requireTwoFactor', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Require Two-Factor Authentication
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.security.sslRequired}
                    onChange={(e) => handleSettingChange('security', 'sslRequired', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    SSL Required
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Settings */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Notification Settings</h3>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.notifications.emailNotifications}
                    onChange={(e) => handleSettingChange('notifications', 'emailNotifications', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <EnvelopeIcon className="ml-2 h-4 w-4 text-gray-400" />
                  <label className="ml-2 block text-sm text-gray-900">
                    Email Notifications
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.notifications.smsNotifications}
                    onChange={(e) => handleSettingChange('notifications', 'smsNotifications', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <DevicePhoneMobileIcon className="ml-2 h-4 w-4 text-gray-400" />
                  <label className="ml-2 block text-sm text-gray-900">
                    SMS Notifications
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.notifications.pushNotifications}
                    onChange={(e) => handleSettingChange('notifications', 'pushNotifications', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <BellIcon className="ml-2 h-4 w-4 text-gray-400" />
                  <label className="ml-2 block text-sm text-gray-900">
                    Push Notifications
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.notifications.adminAlerts}
                    onChange={(e) => handleSettingChange('notifications', 'adminAlerts', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <ExclamationTriangleIcon className="ml-2 h-4 w-4 text-gray-400" />
                  <label className="ml-2 block text-sm text-gray-900">
                    Admin Alerts
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.notifications.userWelcomeEmails}
                    onChange={(e) => handleSettingChange('notifications', 'userWelcomeEmails', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <CheckCircleIcon className="ml-2 h-4 w-4 text-gray-400" />
                  <label className="ml-2 block text-sm text-gray-900">
                    User Welcome Emails
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Integrations Settings */}
          {activeTab === 'integrations' && (
            <div className="space-y-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Third-party Integrations</h3>
              <p className="text-sm text-gray-600">Configure API keys and external service integrations</p>
              
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">OpenAI API Key</label>
                  <input
                    type="password"
                    value={settings.integrations.openaiApiKey}
                    onChange={(e) => handleSettingChange('integrations', 'openaiApiKey', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">Used for AI-powered features like CV generation and personality analysis</p>
                </div>
                
                {/* Stripe Configuration */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-4">💳 Stripe Payment Settings</h4>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Publishable Key</label>
                      <input
                        type="text"
                        value={settings.integrations.stripePublishableKey}
                        onChange={(e) => handleSettingChange('integrations', 'stripePublishableKey', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="pk_test_..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Secret Key</label>
                      <input
                        type="password"
                        value={settings.integrations.stripeSecretKey}
                        onChange={(e) => handleSettingChange('integrations', 'stripeSecretKey', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="sk_test_..."
                      />
                    </div>
                    
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Webhook Secret</label>
                      <input
                        type="password"
                        value={settings.integrations.stripeWebhookSecret}
                        onChange={(e) => handleSettingChange('integrations', 'stripeWebhookSecret', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="whsec_..."
                      />
                    </div>
                  </div>
                </div>

                {/* PayPal Configuration */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-4">🅿️ PayPal Payment Settings</h4>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Client ID</label>
                      <input
                        type="text"
                        value={settings.integrations.paypalClientId}
                        onChange={(e) => handleSettingChange('integrations', 'paypalClientId', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="AY..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Client Secret</label>
                      <input
                        type="password"
                        value={settings.integrations.paypalClientSecret}
                        onChange={(e) => handleSettingChange('integrations', 'paypalClientSecret', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="EL..."
                      />
                    </div>
                    
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Webhook ID</label>
                      <input
                        type="text"
                        value={settings.integrations.paypalWebhookId}
                        onChange={(e) => handleSettingChange('integrations', 'paypalWebhookId', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="WH-..."
                      />
                    </div>
                  </div>
                </div>

                {/* NowPayments (Crypto) Configuration */}
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-4">₿ NowPayments (Crypto) Settings</h4>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">API Key</label>
                      <input
                        type="password"
                        value={settings.integrations.nowpaymentsApiKey}
                        onChange={(e) => handleSettingChange('integrations', 'nowpaymentsApiKey', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Your API key..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">IPN Secret</label>
                      <input
                        type="password"
                        value={settings.integrations.nowpaymentsIpnSecret}
                        onChange={(e) => handleSettingChange('integrations', 'nowpaymentsIpnSecret', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="IPN secret key..."
                      />
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">Supports Bitcoin, Ethereum, and 300+ cryptocurrencies</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Google Analytics ID</label>
                  <input
                    type="text"
                    value={settings.integrations.googleAnalyticsId}
                    onChange={(e) => handleSettingChange('integrations', 'googleAnalyticsId', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Limits Settings */}
          {activeTab === 'limits' && (
            <div className="space-y-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">System Limits</h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Max Users Per Day</label>
                  <input
                    type="number"
                    value={settings.limits.maxUsersPerDay}
                    onChange={(e) => handleSettingChange('limits', 'maxUsersPerDay', parseInt(e.target.value))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Max Job Postings Per Month</label>
                  <input
                    type="number"
                    value={settings.limits.maxJobPostingsPerMonth}
                    onChange={(e) => handleSettingChange('limits', 'maxJobPostingsPerMonth', parseInt(e.target.value))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Max Courses Per User</label>
                  <input
                    type="number"
                    value={settings.limits.maxCoursesPerUser}
                    onChange={(e) => handleSettingChange('limits', 'maxCoursesPerUser', parseInt(e.target.value))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Max File Upload Size (MB)</label>
                  <input
                    type="number"
                    value={settings.limits.maxFileUploadSize}
                    onChange={(e) => handleSettingChange('limits', 'maxFileUploadSize', parseInt(e.target.value))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Backup Settings */}
          {activeTab === 'backup' && (
            <div className="space-y-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Backup Settings</h3>
              
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-5 w-5 text-green-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-800">
                      Last backup: {settings.backup.lastBackup ? new Date(settings.backup.lastBackup).toLocaleString() : 'Never'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.backup.autoBackupEnabled}
                    onChange={(e) => handleSettingChange('backup', 'autoBackupEnabled', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Enable Automatic Backups
                  </label>
                </div>
                
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Backup Frequency</label>
                    <select
                      value={settings.backup.backupFrequency}
                      onChange={(e) => handleSettingChange('backup', 'backupFrequency', e.target.value)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Retention Days</label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={settings.backup.retentionDays}
                      onChange={(e) => handleSettingChange('backup', 'retentionDays', parseInt(e.target.value))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Backup Location</label>
                  <input
                    type="text"
                    value={settings.backup.backupLocation}
                    onChange={(e) => handleSettingChange('backup', 'backupLocation', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="e.g., AWS S3, Google Cloud Storage"
                  />
                </div>
                
                <div>
                  <button
                    type="button"
                    onClick={handleRunBackup}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <CloudIcon className="h-4 w-4 mr-2" />
                    Run Backup Now
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;